import type { Board } from "./board";
import type { Direction } from "./direction";
import { MovementEffect } from "./effects/movementEffect";
import { ParticleEffect } from "./effects/particleEffect";
import type { ParticleType } from "./particleType";
import type { PhysicalThing } from "./physicalThing";
import type { Player } from "./player";
import { Strings } from "./strings";
import { WeaponVisual } from "./weaponVisual";

export abstract class Weapon {
	static key: string;
	ammo: number;
	readonly maxAmmo: number;
	readonly infinite: boolean;
	owner: Player;

	constructor(owner: Player, maxAmmo: number, infinite: boolean = false) {
		this.owner = owner;
		this.maxAmmo = maxAmmo;
		this.infinite = infinite;
		this.ammo = maxAmmo;
	}

	getNameKey(): string {
		return "wname_" + (this.constructor as any).key;
	}

	getName(): string {
		const translated = Strings.get(this.getNameKey());
		if (translated === null) return (this.constructor as any).key;
		return translated;
	}

	getMuzzleFlashType(): ParticleType | null {
		return null;
	}

	getWeaponVisual(): WeaponVisual {
		return WeaponVisual.SMALL_GUN;
	}

	canAutoFire(): boolean {
		return true;
	}

	// Gate only: TAB autofire fires if the closest enemy is within this distance. Melee weapons override to their reach.
	getAutoFireRange(): number {
		return 999;
	}

	getMuzzleFlashCount(): number {
		return 8;
	}

	getMuzzleFlashDirection(direction: Direction): Direction | undefined {
		return direction;
	}

	pullTrigger(direction: Direction, board: Board): boolean {
		if (!this.infinite) {
			if (this.ammo <= 0) {
				board.turnAbortedByPlayer = true;
				return false;
			}
			this.ammo--;
		}
		this.fire(direction, board);
		const weaponKey = (this.constructor as any).key;
		board.stats.recordShotFired(weaponKey);
		const flashType = this.getMuzzleFlashType();
		if (flashType !== null) {
			board.addEffect(new ParticleEffect(this.owner.xy, flashType, this.getMuzzleFlashCount(), this.getMuzzleFlashDirection(direction)));
		}
		return true;
	}

	abstract fire(direction: Direction, board: Board): void;

	protected shotProjectile(board: Board, projectile: PhysicalThing): void {
		if (!board.isInBounds(projectile.xy)) return;
		board.addThing(projectile);
		board.addEffect(new MovementEffect(projectile, this.owner.xy, projectile.xy));
	}

	serialize(): Record<string, unknown> {
		return { type: (this.constructor as any).key, ammo: this.ammo };
	}
}
